import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

const CreateChangeRequestModal = ({ projectId, milestones, open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    stage: '',
    description: '',
    affected_area: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await api.post('/change-requests/create', {
        project_id: projectId,
        ...formData
      });

      toast.success('Change request submitted successfully');
      setFormData({ stage: '', description: '', affected_area: '' });
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create change request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Request Change</DialogTitle>
          <DialogDescription>
            Submit a change request for your project. The engineer will review and provide cost/timeline impact.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="stage">Stage/Activity *</Label>
            <Select
              value={formData.stage}
              onValueChange={(value) => setFormData({ ...formData, stage: value })}
              required
            >
              <SelectTrigger id="stage">
                <SelectValue placeholder="Select stage" />
              </SelectTrigger>
              <SelectContent>
                {milestones && milestones.map((milestone) => (
                  <SelectItem key={milestone.milestone_id} value={milestone.stage_name}>
                    {milestone.stage_name}
                  </SelectItem>
                ))}
                <SelectItem value="General">General / Multiple Stages</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="affected_area">Affected Area *</Label>
            <Input
              id="affected_area"
              value={formData.affected_area}
              onChange={(e) => setFormData({ ...formData, affected_area: e.target.value })}
              placeholder="e.g., Master Bedroom, Kitchen"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the changes you would like to make..."
              rows={4}
              required
            />
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-3">
            <p className="text-sm text-yellow-900">
              <strong>Note:</strong> The engineer will review your request and provide estimated cost and timeline impact before you can approve.
            </p>
          </div>

          <div className="flex space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-orange-700 hover:bg-orange-800"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Submit Request
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateChangeRequestModal;
